import { apiFetch, getActiveCompanyContextId } from '../apiService';
import { API_ENDPOINTS } from '../config';
import type { PaywallConfig, PaywallContext, PaywallFeature } from '../../types/paywall';

// Mirrors the 5-minute TTL used server-side (subscriptions.paywall.CACHE_TTL_SECONDS)
// and the pattern in src/utils/featureToggles.tsx — an in-memory cache keyed
// per context AND product (WS6b), so switching between owner/driver_self_pay or
// between hours/GPS/combo doesn't require a refetch once each has been loaded.
const CACHE_DURATION_MS = 5 * 60 * 1000;

// The owner trial depends on the purchaser company the server resolves from
// the active company context (D-057), so owner entries are also per company.
const cacheKey = (context: PaywallContext, feature: PaywallFeature, companyId: string | null) =>
  context === 'owner' ? `${context}:${feature}:${companyId ?? ''}` : `${context}:${feature}`;

const configCache = new Map<string, { data: PaywallConfig; fetchedAt: number }>();

const isCacheValid = (key: string): boolean => {
  const entry = configCache.get(key);
  if (!entry) return false;
  return Date.now() - entry.fetchedAt < CACHE_DURATION_MS;
};

export const paywallConfigApi = {
  /**
   * Fetch the server-driven paywall config for the given persona context.
   * Authenticated endpoint — the client never sends price/trial, only the
   * persona context ("owner" | "driver_self_pay").
   */
  getConfig: async (
    context: PaywallContext,
    options?: { skipCache?: boolean; feature?: PaywallFeature },
  ): Promise<PaywallConfig> => {
    const feature = options?.feature ?? 'hours';
    const companyId = context === 'owner' ? await getActiveCompanyContextId() : null;
    const key = cacheKey(context, feature, companyId);
    if (!options?.skipCache && isCacheValid(key)) {
      return configCache.get(key)!.data;
    }

    const response = await apiFetch<PaywallConfig>(API_ENDPOINTS.PAYWALL_CONFIG, {
      method: 'GET',
      params: { context, feature },
    });

    if (!response.ok || !response.data) {
      throw new Error('Failed to fetch paywall config');
    }

    configCache.set(key, { data: response.data, fetchedAt: Date.now() });
    return response.data;
  },

  /**
   * Clear the in-memory cache. Useful for tests and for forcing a refetch
   * (e.g. pull-to-refresh on a paywall screen).
   */
  clearCache: (): void => {
    configCache.clear();
  },
};
